import { uploadDiagnosticsRegistry } from "./UploadDiagnosticsRegistry";
import {
  computeHealth,
  buildHealthInput,
  DEFAULT_THRESHOLDS,
} from "./UploadHealthCalculator";
import { HEALTH, UPLOAD_EVENT } from "./UploadEvents";
import { captureDiagnosticSnapshot } from "./DiagnosticSnapshot";
import { decideCapture } from "./StuckDetectorDecide";
import { uploadTelemetryService } from "@/utils/uploadTelemetry";
import { getAnalytics } from "../analytics";

/**
 * Single interval sweeper. Every SWEEP_INTERVAL_MS it:
 *   1. Iterates every UploadDiagnostics in the registry
 *   2. Recomputes health (pure function)
 *   3. If health changed, records a HEALTH_CHANGED event
 *   4. If health transitions INTO POSSIBLY_STUCK, emits UPLOAD_STUCK and
 *      captures a diagnostic snapshot
 *   5. If the upload is terminal (COMPLETED/FAILED), disposes the diagnostics
 *      after a short grace window so the admin drawer can still read it
 *
 * One interval, not N — cheaper than per-upload timers and avoids drift
 * between uploads.
 *
 * The threshold is env-configurable via REACT_APP_UPLOAD_STUCK_THRESHOLD_SECONDS
 * so incident responders can tighten it without a redeploy of application code.
 */

const SWEEP_INTERVAL_MS = 5000;
const TERMINAL_DISPOSE_GRACE_MS = 5 * 60 * 1000;  // keep 5 min for admin drawer
/** @type {Map<string, number>} uploadId → timestamp when it first went terminal */
const terminalMarkedAt = new Map();
/**
 * @type {Map<string, Set<string>>}
 * uploadId → set of trigger names ("stuck" / "failure" / "completed") already
 * captured. Deduping per-trigger — not per-upload — so a stuck capture can be
 * followed by a fresh failure capture on the same upload (backend is
 * latest-wins so the failure snapshot overwrites the stuck one, giving the
 * admin drawer the actual killer error). See StuckDetectorDecide.js.
 */
const capturedTriggersByUploadId = new Map();

const markCaptured = (uploadId, trigger) => {
  let set = capturedTriggersByUploadId.get(uploadId);
  if (!set) {
    set = new Set();
    capturedTriggersByUploadId.set(uploadId, set);
  }
  set.add(trigger);
};

const getCapturedTriggers = (uploadId) =>
  capturedTriggersByUploadId.get(uploadId) || new Set();

let intervalHandle = null;

const readThresholds = () => {
  const raw = Number(process.env.REACT_APP_UPLOAD_STUCK_THRESHOLD_SECONDS);
  if (Number.isFinite(raw) && raw > 0) {
    return { ...DEFAULT_THRESHOLDS, stuckIdleMs: raw * 1000 };
  }
  return DEFAULT_THRESHOLDS;
};

export const startStuckDetector = () => {
  if (intervalHandle !== null) return;
  if (typeof setInterval !== "function") return;
  intervalHandle = setInterval(sweep, SWEEP_INTERVAL_MS);
};

export const stopStuckDetector = () => {
  if (intervalHandle === null) return;
  clearInterval(intervalHandle);
  intervalHandle = null;
};

const sweep = () => {
  const thresholds = readThresholds();
  const now = Date.now();

  for (const diagnostics of uploadDiagnosticsRegistry.all()) {
    try {
      processOne(diagnostics, thresholds, now);
    } catch (_error) {
      // Defensive: sweeper must never die
    }
  }
};

const processOne = (diagnostics, thresholds, now) => {
  const telemetryState = safeTelemetryState(diagnostics.uploadId);
  const input = buildHealthInput(diagnostics, telemetryState);
  const nextHealth = computeHealth(input, thresholds);
  const currentState = diagnostics.getState();
  const previousHealth = currentState.health;

  if (nextHealth !== previousHealth) {
    diagnostics.patchState({ health: nextHealth });
    diagnostics.record(UPLOAD_EVENT.HEALTH_CHANGED, {
      from: previousHealth,
      to: nextHealth,
      idleMs: input.now - (input.lastProgressAt || input.now),
      rollingBps: input.rollingBps ?? null,
      retryCount: input.retryCount,
    });
  }

  // Decide (per-trigger dedupe, pure function — see StuckDetectorDecide.js)
  // whether the current health transition warrants a new snapshot capture.
  const triggerToCapture = decideCapture(
    nextHealth,
    previousHealth,
    getCapturedTriggers(diagnostics.uploadId)
  );

  if (triggerToCapture === "stuck") {
    markCaptured(diagnostics.uploadId, "stuck");
    diagnostics.record(UPLOAD_EVENT.UPLOAD_STUCK, {
      idleMs: input.now - (input.lastProgressAt || input.now),
      retryCount: input.retryCount,
      rollingBps: input.rollingBps ?? null,
    });
    captureDiagnosticSnapshot(diagnostics, {
      trigger: "stuck",
      reason: "no_progress_for_threshold",
    }).catch(() => {});
  } else if (triggerToCapture === "failure") {
    // Fires once per upload when it lands on FAILED. Latest-wins overwrites
    // any earlier "stuck" or "started" snapshot on the backend.
    markCaptured(diagnostics.uploadId, "failure");
    captureDiagnosticSnapshot(diagnostics, {
      trigger: "failure",
      reason: currentState.terminalError || "terminal_failure",
    }).catch(() => {});
  } else if (triggerToCapture === "completed") {
    // Post-mortem for successful uploads — the admin drawer needs full state
    // for happy uploads too, not just failed/stuck ones.
    markCaptured(diagnostics.uploadId, "completed");
    captureDiagnosticSnapshot(diagnostics, {
      trigger: "completed",
      reason: "terminal_success",
    }).catch(() => {});
  }

  // Terminal cleanup with grace window
  if (
    nextHealth === HEALTH.COMPLETED ||
    nextHealth === HEALTH.FAILED ||
    nextHealth === HEALTH.CANCELLED
  ) {
    if (!terminalMarkedAt.has(diagnostics.uploadId)) {
      terminalMarkedAt.set(diagnostics.uploadId, now);
      // Clear activeUploadId person-property once nothing is in-flight.
      const hasActive = uploadDiagnosticsRegistry
        .all()
        .some(
          (d) =>
            d.uploadId !== diagnostics.uploadId &&
            d.getState().uploaderState !== "completed" &&
            d.getState().uploaderState !== "failed" &&
            d.getState().uploaderState !== "cancelled"
        );
      if (!hasActive) {
        getAnalytics().setPersonProperties({ activeUploadId: null });
      }
    }
    const markedAt = terminalMarkedAt.get(diagnostics.uploadId);
    if (now - markedAt > TERMINAL_DISPOSE_GRACE_MS) {
      terminalMarkedAt.delete(diagnostics.uploadId);
      capturedTriggersByUploadId.delete(diagnostics.uploadId);
      uploadDiagnosticsRegistry.dispose(diagnostics.uploadId, {
        // Keep the persisted timeline for a while so a hard refresh can
        // still see it — TimelineStorage.prune() takes care of true cleanup.
        removeStoredTimeline: false,
      });
    }
  }
};

const safeTelemetryState = (uploadId) => {
  try {
    return uploadTelemetryService.getState(uploadId);
  } catch (_error) {
    return null;
  }
};

// Test-only reset.
export const __resetStuckDetectorForTests = () => {
  stopStuckDetector();
  terminalMarkedAt.clear();
  capturedTriggersByUploadId.clear();
};
